import { Component, OnInit, OnDestroy } from '@angular/core';
import { ProgramCategoryList } from 'src/app/program/model/programCategoryList';
import { ProgramCategoryService } from 'src/app/core/service/program-category.service';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Filter } from 'src/app/core/filter/filter';
import { ProgramService } from 'src/app/core/service/program.service';
import { ChannelService } from 'src/app/core/service/channel.service';
import { FilterService } from 'src/app/core/filter/filter.service';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { NGXLogger } from 'ngx-logger';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { SettingService } from 'src/app/core/service/setting.service';

@Component({
  selector: 'app-dashboard-coordinator',
  templateUrl: './dashboard-coordinator.component.html',
  styleUrls: ['./dashboard-coordinator.component.scss']
})
export class DashboardCoordinatorComponent implements OnInit, OnDestroy
{
  public breadcrumb = 'ALL';
  public stringLiteralBreadcrumb = false;
  /**
   * List of Group By selection
   */
  public listGroupBy: Array<{ text: string; value: string }>;

  /**
   * List of Channel Statuses selection
   */
  public channelStatuses: Array<{ text: string; value: string }>;

  /**
   * List of Channel Sorts selection
   */
  public channelSorts: Array<{ text: string; value: string }>;

  public loading = true;

  public filterForm: any;

  /**
   * Program Categories for drop-down.
   */
  public programCategories: ProgramCategoryList[] = [];

  public showFilterBtnActions = false;

  public filterSelected: Filter;

  public filterToApply: any;

  public groupFilter: string;

  public sortedBy: string;

  public sortedDirection: string;

  public filterAll = true;

  public filterDialogOpened = false;

  public collapseAll = true;

  public configurableDashboard = [];

  /**
   * Subscription to Angular component route parameters.
   */
  private routeParamSubscription: Subscription;
  private messagingSubscription: Subscription;
  private disabledApplyFilters = false;
  private breadcrumbKey = 'ALL';

  constructor(
    private programCategoryService: ProgramCategoryService,
    private programService: ProgramService,
    private channelService: ChannelService,
    private filterService: FilterService,
    private logger: NGXLogger,
    private authenticationService: AuthenticationService,
    private route: ActivatedRoute,
    private messageHandlerService: MessageHandlerService,
    private messengerService: MessengerService,
    private fb: UntypedFormBuilder,
    private settingService: SettingService
  )
  {
    this.listGroupBy = this.programService.listGroupBy.filter(x => x.value !== 'performance');
    this.channelSorts = this.channelService.channelSortsDashboard;
    this.channelStatuses = this.channelService.channelStatuses;

    // get list of countries from Dictionary table
    this.programCategoryService.getCachedProgramCategories(true).subscribe({
      next: (data) =>
      {
        this.programCategories = data;
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'dashboardGroupCollapsedParent')
        {
          this.collapseAll = message.data;
        }
      }
    });

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.configurableDashboard = sessionSetting.configurableDashboard ? sessionSetting.configurableDashboard.split(',') : [];
      }
    });

    this.initFilterForm();
    this.loading = false;
  }

  /**
   * Return true if channels are grouped.
   */
  public get isGrouped(): boolean
  {
    return (this.groupBy.value !== undefined &&
      this.groupBy.value !== null && this.groupBy.value !== '');
  }

  /**
   * Return true if channels are sorted.
   */
  public get isSorted(): boolean
  {
    return (this.sortBy.value !== undefined &&
      this.sortBy.value !== null && this.sortBy.value !== '');
  }

  /**
   * GroupBy form control.
   *
   * @returns groupBy form control.
   */
  public get groupBy(): UntypedFormControl
  {
    return this.filterForm.get('groupBy');
  }

  /**
   * FilterByCategory form control.
   *
   * @returns filterByCategory form control.
   */
  public get filterByCategory(): UntypedFormControl
  {
    return this.filterForm.get('filterByCategory');
  }

  /**
   * FilterByStatus form control.
   *
   * @returns filterByStatus form control.
   */
  public get filterByStatus(): UntypedFormControl
  {
    return this.filterForm.get('filterByStatus');
  }

  /**
   * FilterByProgramName form control.
   *
   * @returns FilterByProgramName form control.
   */
  public get filterByProgramName(): UntypedFormControl
  {
    return this.filterForm.get('filterByProgramName');
  }

  /**
   * FilterByChannelName form control.
   *
   * @returns FilterByChannelName form control.
   */
  public get filterByChannelName(): UntypedFormControl
  {
    return this.filterForm.get('filterByChannelName');
  }

  /**
   * SortBy form control.
   *
   * @returns sortBy form control.
   */
  public get sortBy(): UntypedFormControl
  {
    return this.filterForm.get('sortBy');
  }

  /**
   * SortDirection form control.
   *
   * @returns sortDirection form control.
   */
  public get sortDirection(): UntypedFormControl
  {
    return this.filterForm.get('sortDirection');
  }

  public isConfigurableDashboardVisible(item: string): boolean
  {
    return this.configurableDashboard.indexOf(item) !== -1;
  }

  public ngOnInit(): void
  {
    this.routeParamSubscription = this.route.params.subscribe({
      next: (params) =>
      {
        this.filterSelected = undefined;
        if (params.filter === 'all')
        {
          this.filterAll = true;
          this.setFilters();
        }
        else
        {
          this.filterAll = false;
          const currentUser = this.authenticationService.currentUser;
          this.filterService.getCachedDashboardFilters(currentUser.userName).subscribe({
            next: (filters: Filter[]) =>
            {
              this.setFilters(params.filter, filters);
            },
            error: (error) =>
            {
              this.logger.error(error);
            }
          });
        }
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.routeParamSubscription !== undefined) { this.routeParamSubscription.unsubscribe(); }
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  /**
   * Check if item is selected.
   *
   * @param filterItem Filter to check.
   * @param item Item value to check.
   */
  public isFilterSelected(filterItem: string, itemValue: any): boolean
  {
    return this[filterItem] && this[filterItem].value && this[filterItem].value.includes(itemValue);
  }

  /**
   * Return true a filtered for the filterItem.
   *
   * @param filterItem Filter to check.
   */
  public isFiltered(filterItem: string): boolean
  {
    return this[filterItem] && this[filterItem].value && this[filterItem].value.length > 0;
  }

  public updateProgramCollapse(group: string, event: boolean): void
  {
    const message: any = {};
    message.group = group;
    message.event = event;
    this.messengerService.sendMessage(new MessageSubject('dashboardGroupCollapsedChild', message));
    if (!event)
    {
      this.collapseAll = false;
    }
  }

  /**
   * Clear all filters.
   */
  public clearFilters(): void
  {
    this.filterForm.reset();
    this.showFilterBtnActions = false;
  }

  public openFilterScreen(): void
  {
    if (this.filterAll)
    {
      this.filterSelected = undefined;
    }
    this.filterDialogOpened = true;
  }

  /**
   * Apply Channel group, filters and sorts.
   */
  private applyFilters(): void
  {
    if (this.disabledApplyFilters)
    {
      return;
    }

    this.showFilterBtnActions = false;

    // Apply filters
    if (this.isFiltered('filterByCategory') || this.isFiltered('filterByStatus') ||
      this.isFiltered('filterByProgramName') || this.isFiltered('filterByChannelName'))
    {
      this.showFilterBtnActions = true;
    }

    // Create channel groups if needed
    if (this.isGrouped)
    {
      this.groupFilter = this.filterForm.get('groupBy').value;
      this.showFilterBtnActions = true;
    }
    else
    {
      this.groupFilter = this.filterForm.get('groupBy').value;
    }

    if (this.isSorted)
    {
      this.sortedBy = this.sortBy.value;
      this.sortedDirection = this.sortDirection.value;
      this.showFilterBtnActions = true;
    }

    this.filterToApply =
    {
      filterCategory: this.filterForm.get('filterByCategory').value,
      filterByStatus: this.filterForm.get('filterByStatus').value,
      filterByProgramName: this.filterForm.get('filterByProgramName').value,
      filterByChannelName: this.filterForm.get('filterByChannelName').value,
    };
  }

  /**
   * Initialization of the filter form controls.
   */
  private initFilterForm(): void
  {
    // Create form controls
    this.filterForm = this.fb.group({
      groupBy: null,
      sortBy: null,
      sortDirection: null,
      filterByCategory: null,
      filterByStatus: null,
      filterByProgramName: null,
      filterByChannelName: null
    });

    // Register group/filter changes subscriptions
    this.filterForm.get('groupBy').valueChanges.subscribe({
      next: () =>
      {
        this.applyFilters();
      }
    });

    this.filterForm.get('sortBy').valueChanges.subscribe({
      next: (sortBySelected: string) =>
      {
        // Set default sort direction
        let sortDirection = '';
        if (sortBySelected && sortBySelected !== undefined)
        {
          switch (sortBySelected)
          {
            case 'name':        // Program name
            case 'createdOn':
              sortDirection = 'asc';
              break;
            default:
              sortDirection = 'desc';
          }
        }
        this.sortDirection.setValue(sortDirection);
        this.applyFilters();
      }
    });

    this.filterForm.get('sortDirection').valueChanges.subscribe({
      next: () =>
      {
        this.applyFilters();
      }
    });

    this.filterForm.get('filterByCategory').valueChanges.subscribe({
      next: () =>
      {
        this.applyFilters();
      }
    });

    this.filterForm.get('filterByStatus').valueChanges.subscribe({
      next: () =>
      {
        this.applyFilters();
      }
    });

    this.filterForm.get('filterByProgramName').valueChanges.subscribe({
      next: () => { this.applyFilters(); }
    });

    this.filterForm.get('filterByChannelName').valueChanges.subscribe({
      next: () =>
      {
        this.applyFilters();
      }
    });
  }

  /**
   * Set the filter form criteria and apply them when all set only.
   *
   * @param filter Saved filter.
   */
  private setFilters(filterId: string = null, filters: Filter[] = null): void
  {
    try
    {
      this.disabledApplyFilters = true;    // Disable changes event
      this.clearFilters();

      // If filter to set
      if (filterId && filters)
      {
        const filterItem = filters.find(obj => obj.id.toString() === filterId.toString());
        this.filterSelected = filterItem;
        if (filterItem)
        {
          const state = JSON.parse(filterItem.state);
          this.groupBy.setValue(state.groupBy);
          this.sortBy.setValue(state.sortBy);
          this.sortDirection.setValue(state.sortDirection);
          this.filterByCategory.setValue(state.filterByCategory);
          this.filterByStatus.setValue(state.filterByStatus);
          this.filterByProgramName.setValue(state.filterByProgramName);
          this.filterByChannelName.setValue(state.filterByChannelName);
          this.breadcrumb = filterItem.name;
          this.stringLiteralBreadcrumb = true;
        }
      }
      else
      {
        this.breadcrumb = this.breadcrumbKey;
        this.stringLiteralBreadcrumb = false;
        this.filterByStatus.setValue(['Online']);
        this.groupBy.setValue('program');
      }
    }
    finally
    {
      this.disabledApplyFilters = false;   // Enabled changes event
      // Apply Group, Filter and Sort (if any)
      setTimeout(() => { this.applyFilters(); });
    }
  }
}
